import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
	ShoppingCart as ShoppingCartIcon,
	ArrowLeft,
	Minus,
	Plus,
	Trash2,
	ImageOff,
	Shield,
	Truck,
	RotateCcw,
	Star,
	Gift,
	CreditCard,
	Loader2,
	AlertCircle,
	Check,
	X,
} from 'lucide-react'
import Layout from '@/components/layout/layout'
import Toast from '@/components/elements/toast'
import { AppDispatch, RootState } from '@/store/store'
import {
	updateCartQuantity,
	removeFromCart,
	clearCart,
	initializeCartTotals,
} from '@/store/features/cart'
import CartEntity from '@/entities/api/cart/cart'
import {
	attributeMaps,
	colorsMap,
	sizeMap,
	flavorMap,
	objectivesMap,
	modalitiesMap,
	NameAttributes,
} from '@/entities/api/products/product'
import { API_URL } from '@/services/api/keys'
import { verifyStockOfAttribute } from '@/services/api/products/attributes'
import formatCurrency from '@/utils/price-convert'
import EmptyCart from '@/components/elements/empty-cart'
import ProductSlider from '@/components/sliders/products-slider'
import { filterByCategoryAndType } from '@/services/api/products/products-filter'
import { FilteredProductsResponse } from '@/entities/api/products/product-filters'
import { Entities } from '@/entities/api/entities'
import CustomersEntity from '@/entities/api/customers/customer'
import {
	deleteCartProductById,
	updateCartProductQuantity,
	voidCartByCustomer,
} from '@/services/api/cart/cart'
import { ToastConfirm } from '@/components/elements/toast-confirm'
import AuthRequiredScreen from '@/components/default/auth-required'
import { getCouponByCode } from '@/services/api/coupons/coupons'
import CouponsEntity, { TypesCoupon } from '@/entities/api/coupons/coupon'
import { setOrdersFn } from '@/store/features/orders'
import useCartCalculations from '@/hooks/useCartCalculations'

interface SelectedItems {
	[cartId: string]: boolean
}

interface LoadingStates {
	[key: string]: boolean
}

const ShoppingCart = ({
	customer,
	token,
}: {
	customer: CustomersEntity | null
	token: string | null
}) => {
	const dispatch = useDispatch<AppDispatch>()
	const navigate = useNavigate()
	const { cart, totalItems } = useSelector((state: RootState) => state.cart)

	const [products, setProducts] = useState<FilteredProductsResponse | null>()
	const [selectedItems, setSelectedItems] = useState<SelectedItems>({})
	const [loadingStates, setLoadingStates] = useState<LoadingStates>({})
	const [isProcessing, setIsProcessing] = useState(false)
	const [promoCode, setPromoCode] = useState('')
	const [coupon, setCoupon] = useState<Partial<CouponsEntity> | null>(null)
	const [isCouponLoading, setIsCouponLoading] = useState(false)

	const selectedCartItems = cart.filter(
		(item) => selectedItems[item?._id ?? '']
	)

	const calculatedData = useCartCalculations({
		items: selectedCartItems,
		coupon: coupon,
	})

	const hasSelectedItems = selectedCartItems.length > 0

	useEffect(() => {
		dispatch(initializeCartTotals())
	}, [dispatch])

	useEffect(() => {
		const initialSelection: SelectedItems = {}
		cart.forEach((item) => {
			initialSelection[item?._id ?? ''] = true
		})
		setSelectedItems(initialSelection)
	}, [cart])

	useEffect(() => {
		const getPromotions = async () => {
			try {

				const response = await filterByCategoryAndType({
					type: Entities.SUPPLEMENTS,
					page: 1,
					limit: 24,
				})

				if ([400, 401, 409, 404].includes(response?.status ?? 0)) {
					Toast({
						type: 'error',
						message: 'No se pudo obtener el producto, inténtalo más tarde.',
					})
					return
				}

				setProducts(response?.data)
			} catch (error) {
				Toast({ type: 'error', message: 'Error al obtener los productos' })
			}
		}

		getPromotions()
	}, [])

	const isCouponValid = (coupon: Partial<CouponsEntity>): boolean => {
		if (!coupon.expiration) return false
		const expirationDate = new Date(coupon.expiration)
		const currentDate = new Date()
		return expirationDate > currentDate
	}

	const calculateItemTotal = (item: CartEntity): number => {
		const attribute = item.product.attributes?.[0]
		if (!attribute) return 0

		const price = attribute.price || 0
		const quantity = attribute.quantity || 0
		const discount = item.product.discount || 0

		const discountedPrice = price - (price * discount) / 100
		return discountedPrice * quantity
	}

	const calculateItemShipping = (item: CartEntity): number => {
		if (!item.product.shipping_info) return 0

		const shippingInfo = item.product.shipping_info
		const attribute = item.product.attributes?.[0]
		const quantity = attribute?.quantity || 0

		if (
			!shippingInfo.isHaveShipping ||
			shippingInfo.type === 'free' ||
			quantity === 0
		) {
			return 0
		}

		if (shippingInfo.type === 'pay') {
			const baseShippingPrice = shippingInfo.price || 0
			const totalAttributes =
				item.product.attributes?.reduce((acc, attr) => {
					return acc + (attr.quantity || 0)
				}, 0) || 0

			let totalShipping = baseShippingPrice

			if (totalAttributes > 1) {
				const additionalAttributes = totalAttributes - 1
				totalShipping += baseShippingPrice * 0.5 * additionalAttributes
			}

			return totalShipping
		}

		return 0
	}

	const handleQuantityChange = async (
		productId: string,
		attributeId: string,
		newQuantity: number,
		cartId: string
	) => {
		if (newQuantity <= 0) return

		const loadingKey = `${cartId}-quantity`
		setLoadingStates((prev) => ({ ...prev, [loadingKey]: true }))

		try {
			const response = await verifyStockOfAttribute(
				productId ?? '',
				attributeId ?? ''
			)

			if ([400, 401, 409, 404].includes(response?.status ?? 0)) {
				Toast({
					type: 'error',
					message: 'El producto no se puede agregar al carrito',
				})
				return
			}

			const { quantity: quantityFromApi } = response?.data

			if (newQuantity > quantityFromApi) {
				Toast({
					type: 'info',
					message: `Solo quedan ${quantityFromApi} unidad(es) disponibles`,
				})
				return
			}

			if (customer?._id && token) {
				const responseChange = await updateCartProductQuantity(
					cartId,
					productId,
					attributeId,
					{ quantity: newQuantity },
					token
				)

				if ([400, 404, 401, 409].includes(responseChange.status ?? 0)) {
					Toast({ type: 'error', message: 'No se pudo actualizar la cantidad' })
				}
			}

			dispatch(
				updateCartQuantity({ productId, attributeId, quantity: newQuantity })
			)
		} catch (error) {
			Toast({
				type: 'error',
				message: 'Error al verificar el stock del producto',
			})
		} finally {
			setLoadingStates((prev) => ({ ...prev, [loadingKey]: false }))
		}
	}

	const handleRemoveItem = (cartId: string) => {
		ToastConfirm({
			message: '¿Estas seguro que quieres eliminar el producto del carrito?',
			onConfirm: async () => {
				if (customer?._id && token) {
					const response = await deleteCartProductById(cartId, token)
					if ([400, 401, 404, 409].includes(response?.status ?? 0)) {
						Toast({ type: 'error', message: 'No se pudo vaciar el carrito' })
						return
					}
				}
				dispatch(removeFromCart({ cartId }))
				setSelectedItems((prev) => {
					const newSelection = { ...prev }
					delete newSelection[cartId]
					return newSelection
				})
				Toast({
					type: 'success',
					message: 'Producto eliminado del carrito',
				})
			},
			onCancel: async () => {
				Toast({ type: 'info', message: 'Se cancelo la operacion' })
			},
		})
	}

	const handleClearCart = () => {
		ToastConfirm({
			message: '¿Estas seguro que quieres vaciar el carrito?',
			onConfirm: async () => {
				if (customer?._id && token) {
					const response = await voidCartByCustomer(customer?._id, token)
					if ([400, 401, 404, 409].includes(response?.status ?? 0)) {
						Toast({ type: 'error', message: 'No se pudo vaciar el carrito' })
						return
					}
				}
				dispatch(clearCart())
				setSelectedItems({})
				setCoupon(null)
				setPromoCode('')
				Toast({
					type: 'success',
					message: 'Carrito vaciado exitosamente',
				})
			},
			onCancel: async () => {
				Toast({ type: 'info', message: 'Se cancelo la operacion' })
			},
		})
	}

	const handleSelectItem = (cartId: string) => {
		setSelectedItems((prev) => ({
			...prev,
			[cartId]: !prev[cartId],
		}))
	}

	const handleSelectAll = () => {
		const allSelected = Object.values(selectedItems).every(Boolean)
		const newSelection: SelectedItems = {}
		cart.forEach((item) => {
			newSelection[item?._id ?? ''] = !allSelected
		})
		setSelectedItems(newSelection)
	}

	const getAttributeDisplayValue = (attribute: any): string => {
		try {
			const values = Array.isArray(attribute.value)
				? attribute.value
				: [attribute.value]
			const names = Array.isArray(attribute.name)
				? attribute.name
				: [attribute.name]

			if (values.length === 0) return ''

			const displayValues = values.map((value: any, index: number) => {
				const name = names[index] || names[0]
				const valueStr = String(value)

				switch (name) {
					case NameAttributes.COLOR:
						return colorsMap[valueStr as keyof typeof colorsMap]
							? valueStr
							: valueStr
					case NameAttributes.SIZE:
						return sizeMap[valueStr as keyof typeof sizeMap]
							? `${valueStr} (${sizeMap[valueStr as keyof typeof sizeMap]})`
							: valueStr
					case NameAttributes.FLAVOR:
						return flavorMap[valueStr as keyof typeof flavorMap] || valueStr
					case NameAttributes.OBJECTIVE:
						return (
							objectivesMap[valueStr as keyof typeof objectivesMap] || valueStr
						)
					case NameAttributes.MODALITY:
						return (
							modalitiesMap[valueStr as keyof typeof modalitiesMap] || valueStr
						)
					default:
						return (
							attributeMaps[valueStr as keyof typeof attributeMaps] || valueStr
						)
				}
			})

			return displayValues.join(' / ')
		} catch (error) {
			const fallbackValue = Array.isArray(attribute.value)
				? attribute.value[0]
				: attribute.value
			return (
				attributeMaps[String(fallbackValue) as keyof typeof attributeMaps] ||
				String(fallbackValue || '')
			)
		}
	}

	const handleProceedToCheckout = () => {
		if (!hasSelectedItems) {
			Toast({
				type: 'warning',
				message: 'Selecciona al menos un producto para continuar',
			})
			return
		}
		setIsProcessing(true)
		setTimeout(() => {
			setIsProcessing(false)
			dispatch(
				setOrdersFn({
					items: selectedCartItems,
					totalItems: calculatedData.totalItems,
					totalPrice: calculatedData.totalPrice,
					totalShipping: calculatedData.totalShipping,
					couponDiscount: calculatedData.couponDiscount,
					subtotalAfterCoupon: calculatedData.subtotalAfterCoupon,
					grandTotal: calculatedData.grandTotal,
					coupon: coupon,
				})
			)
			navigate('/Pago')
		}, 1000)
	}

	const handleApplyPromoCode = async () => {
		if (!promoCode.trim()) {
			Toast({ type: 'warning', message: 'Ingresa un código de cupón' })
			return
		}

		setIsCouponLoading(true)
		try {
			const response = await getCouponByCode(promoCode, token ?? '')

			if ([400, 401, 404, 409].includes(response?.status ?? 0)) {
				Toast({ type: 'error', message: 'Cupón no encontrado o inválido' })
				setIsCouponLoading(false)
				return
			}

			const couponData = response?.data

			if (!isCouponValid(couponData)) {
				Toast({ type: 'error', message: 'El cupón ha expirado' })
				setIsCouponLoading(false)
				return
			}

			setCoupon(couponData)
			Toast({ type: 'success', message: 'Cupón aplicado exitosamente' })
		} catch (error) {
			Toast({ type: 'error', message: 'Error al aplicar el cupón' })
		} finally {
			setIsCouponLoading(false)
		}
	}

	const handleRemoveCoupon = () => {
		setCoupon(null)
		setPromoCode('')
		Toast({ type: 'info', message: 'Cupón removido' })
	}

	if (!customer || !token) {
		return (
			<Layout customer={null} token={null}>
				<AuthRequiredScreen />
			</Layout>
		)
	}

	if (cart.length === 0) {
		return <EmptyCart products={products as FilteredProductsResponse} />
	}

	return (
		<Layout customer={customer} token={token}>
			<div className='bg-gradient-to-b from-gray-100 via-gray-50 to-white py-[4vh] px-[2vw] lg:px-[6vw]'>
				<div className='max-w-full mx-auto px-4 sm:px-6'>
					<div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8'>
						<div className='flex items-center gap-3'>
							<button
								onClick={() => navigate(-1)}
								className='p-2 cursor-pointer hover:bg-white rounded-xl transition-all'
							>
								<ArrowLeft className='w-5 h-5 text-gray-600' />
							</button>
							<div>
								<h1 className='text-2xl sm:text-3xl font-bold text-gray-900'>
									Carrito
								</h1>
								<p className='text-gray-600 text-sm'>{totalItems} productos</p>
							</div>
						</div>

						<div className='flex items-center gap-3 text-sm'>
							<button
								onClick={handleSelectAll}
								className='text-red-600 hover:text-red-700 font-medium'
							>
								{Object.values(selectedItems).every(Boolean)
									? 'Deseleccionar'
									: 'Seleccionar todo'}
							</button>
							<span className='text-gray-300'>|</span>
							<button
								onClick={handleClearCart}
								className='text-gray-500 hover:text-red-600 transition-colors'
							>
								Vaciar carrito
							</button>
						</div>
					</div>

					<div className='grid grid-cols-1 xl:grid-cols-4 gap-6 lg:gap-8'>
						<div className='xl:col-span-3 space-y-3 sm:space-y-4'>
							{cart.map((item) => {
								const attribute = item.product.attributes?.[0]
								if (!attribute) return null

								const itemTotal = calculateItemTotal(item)
								const itemShipping = calculateItemShipping(item)
								const originalPrice = attribute.price || 0
								const discount = item.product.discount || 0
								const discountedPrice =
									originalPrice - (originalPrice * discount) / 100
								const isSelected = selectedItems[item?._id ?? '']
								const isLoading = loadingStates[`${item._id}-quantity`]

								const totalAttributes = item.product.attributes?.length || 1
								const hasMultipleAttributes = totalAttributes > 1

								return (
									<div
										key={item._id}
										className={`bg-white rounded-3xl transition-all duration-200 p-4 sm:p-6 ${
											isSelected
												? 'ring-2 ring-red-100 bg-red-50/30'
												: 'hover:shadow-sm'
										}`}
									>
										<div className='flex gap-4'>
											<div className='flex items-start pt-1'>
												<div className='relative'>
													<input
														type='checkbox'
														checked={isSelected}
														onChange={() => handleSelectItem(item?._id ?? '')}
														className='sr-only'
													/>
													<div
														onClick={() => handleSelectItem(item?._id ?? '')}
														className={`w-5 h-5 rounded-md border-2 cursor-pointer transition-all ${
															isSelected
																? 'bg-red-600 border-red-600'
																: 'border-gray-300 hover:border-red-300'
														}`}
													>
														{isSelected && (
															<Check className='w-3 h-3 text-white absolute top-0.5 left-0.5' />
														)}
													</div>
												</div>
											</div>

											<div className='w-20 h-20 sm:w-24 sm:h-24 bg-gray-100 rounded-2xl overflow-hidden flex-shrink-0'>
												{attribute.image?.url ? (
													<img
														src={`${API_URL}/${attribute.image.url}`}
														alt={item.product.name}
														className='w-full h-full object-cover'
													/>
												) : (
													<div className='w-full h-full flex items-center justify-center'>
														<ImageOff className='w-6 h-6 sm:w-8 sm:h-8 text-gray-400' />
													</div>
												)}
											</div>

											<div className='flex-1 min-w-0'>
												<div className='flex justify-between items-start mb-3'>
													<div className='flex-1 pr-2'>
														<Link
															to={`/producto/${item.product._id}`}
															className='font-bold text-gray-900 hover:text-red-600 transition-colors text-sm sm:text-base line-clamp-2'
														>
															{item.product.name}
														</Link>
														<p className='text-gray-600 text-xs sm:text-sm mt-1'>
															{item.product.brand}
														</p>
													</div>
													<button
														onClick={() => handleRemoveItem(item?._id ?? '')}
														className='p-2 hover:bg-red-50 hover:text-red-600 rounded-xl transition-all text-gray-400'
													>
														<Trash2 className='w-4 h-4' />
													</button>
												</div>

												<div className='flex items-center justify-between flex-wrap gap-3'>
													<div className='flex items-center gap-2'>
														<span className='text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-lg font-medium'>
															{getAttributeDisplayValue(attribute)}
														</span>
														{discount > 0 && (
															<span className='text-xs bg-red-100 text-red-700 px-2 py-1 rounded-lg font-bold'>
																-{discount}%
															</span>
														)}
														{hasMultipleAttributes && (
															<span className='text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-lg font-medium'>
																{totalAttributes} variantes
															</span>
														)}
													</div>

													<div className='flex items-center gap-3'>
														<div className='flex items-center bg-gray-100 rounded-xl'>
															<button
																onClick={() =>
																	handleQuantityChange(
																		item.product._id!,
																		attribute._id!,
																		(attribute.quantity || 1) - 1,
																		item?._id ?? ''
																	)
																}
																className='p-2 hover:bg-gray-200 transition-colors disabled:opacity-50 rounded-l-xl'
																disabled={attribute.quantity <= 1 || isLoading}
															>
																<Minus className='w-3 h-3 text-gray-600' />
															</button>
															<div className='px-3 py-2 min-w-[2.5rem] text-center'>
																{isLoading ? (
																	<Loader2 className='w-3 h-3 animate-spin mx-auto text-red-600' />
																) : (
																	<span className='text-sm font-bold text-gray-900'>
																		{attribute.quantity || 0}
																	</span>
																)}
															</div>
															<button
																onClick={() =>
																	handleQuantityChange(
																		item.product._id!,
																		attribute._id!,
																		(attribute.quantity || 0) + 1,
																		item?._id ?? ''
																	)
																}
																className='p-2 hover:bg-gray-200 transition-colors rounded-r-xl'
																disabled={isLoading}
															>
																<Plus className='w-3 h-3 text-gray-600' />
															</button>
														</div>
													</div>
												</div>

												<div className='flex items-center justify-between mt-3 pt-3 border-t border-gray-100'>
													<div className='flex flex-col'>
														{discount > 0 ? (
															<>
																<span className='text-lg font-bold text-red-600'>
																	{formatCurrency(discountedPrice)}
																</span>
																<span className='text-xs text-gray-400 line-through'>
																	{formatCurrency(originalPrice)}
																</span>
															</>
														) : (
															<span className='text-lg font-bold text-gray-900'>
																{formatCurrency(originalPrice)}
															</span>
														)}
														{itemShipping > 0 && (
															<div className='flex items-center gap-1 mt-1'>
																<Truck className='w-3 h-3 text-green-600' />
																<span className='text-xs text-green-600 font-medium'>
																	+{formatCurrency(itemShipping)}
																	{hasMultipleAttributes && (
																		<span className='text-xs text-blue-600 ml-1'>
																			(+{(totalAttributes - 1) * 50}% envío)
																		</span>
																	)}
																</span>
															</div>
														)}
													</div>
													<div className='text-right'>
														<span className='text-lg font-bold text-gray-900'>
															{formatCurrency(itemTotal)}
														</span>
														{itemShipping > 0 && (
															<div className='text-xs text-gray-500 mt-0.5'>
																Total:{' '}
																{formatCurrency(itemTotal + itemShipping)}
															</div>
														)}
													</div>
												</div>
											</div>
										</div>
									</div>
								)
							})}
						</div>

						<div className='xl:col-span-1'>
							<div className='sticky top-8 space-y-4'>
								<div className='bg-white rounded-3xl p-6 shadow-md'>
									<h3 className='text-lg font-bold text-gray-900 mb-4 flex items-center gap-2'>
										<ShoppingCartIcon className='w-5 h-5 text-red-600' />
										Resumen
									</h3>

									<div className='space-y-3 mb-6'>
										<div className='flex justify-between text-sm text-gray-600'>
											<span>Productos:</span>
											<span className='font-semibold'>
												{calculatedData.totalItems}
											</span>
										</div>
										<div className='flex justify-between text-sm text-gray-600'>
											<span>Subtotal:</span>
											<span className='font-semibold'>
												{formatCurrency(calculatedData.totalPrice)}
											</span>
										</div>

										{coupon && calculatedData.couponDiscount > 0 && (
											<div className='flex justify-between text-sm text-green-600'>
												<span className='flex items-center gap-1'>
													<Gift className='w-3 h-3' />
													Descuento cupón:
												</span>
												<span className='font-semibold'>
													-{formatCurrency(calculatedData.couponDiscount)}
												</span>
											</div>
										)}

										<div className='flex justify-between text-sm'>
											<div className='flex items-center gap-1'>
												<Truck className='w-4 h-4 text-green-600' />
												<span className='text-gray-600'>Envío:</span>
											</div>
											<span
												className={`font-semibold ${
													calculatedData.totalShipping > 0
														? 'text-green-600'
														: 'text-green-600'
												}`}
											>
												{calculatedData.totalShipping > 0
													? formatCurrency(calculatedData.totalShipping)
													: 'GRATIS'}
											</span>
										</div>
										<div className='border-t border-gray-200 pt-3'>
											<div className='flex justify-between text-lg font-bold text-gray-900'>
												<span>Total:</span>
												<span className='text-red-600'>
													{formatCurrency(calculatedData.grandTotal)}
												</span>
											</div>
										</div>
									</div>

									<div className='space-y-3'>
										<button
											onClick={handleProceedToCheckout}
											disabled={!hasSelectedItems || isProcessing}
											className='w-full cursor-pointer bg-red-600 hover:bg-red-700 text-white py-4 rounded-2xl font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2'
										>
											{isProcessing ? (
												<>
													<Loader2 className='w-5 h-5 animate-spin' />
													Procesando...
												</>
											) : (
												<>
													<CreditCard className='w-5 h-5' />
													Pagar
												</>
											)}
										</button>

										{!hasSelectedItems && (
											<div className='flex items-center gap-2 text-amber-600 text-sm justify-center'>
												<AlertCircle className='w-4 h-4' />
												<span>Selecciona productos</span>
											</div>
										)}

										<Link
											to='/'
											className='block w-full text-center text-gray-600 hover:text-gray-800 py-3 text-sm font-medium transition-colors hover:bg-gray-50 rounded-2xl'
										>
											Seguir comprando
										</Link>
									</div>
								</div>

								<div className='bg-red-50 rounded-3xl border border-red-100 p-6 shadow-md'>
									<div className='flex items-center gap-2 mb-3'>
										<Gift className='w-5 h-5 text-red-600' />
										<h4 className='font-bold text-red-900 text-sm'>
											Cupón de descuento
										</h4>
									</div>

									{coupon ? (
										<div className='space-y-3'>
											<div className='bg-green-100 border border-green-200 rounded-lg sm:rounded-xl p-3'>
												<div className='flex items-start justify-between gap-2'>
													<div className='flex items-start gap-2 flex-1 min-w-0'>
														<Check className='w-4 h-4 text-green-600 flex-shrink-0 mt-0.5' />
														<div className='flex-1 min-w-0'>
															<span className='text-sm font-medium text-green-800 block'>
																Cupón aplicado
															</span>
															<p className='text-xs text-green-700 font-mono mt-1 break-all'>
																{coupon.code}
															</p>
															<p className='text-xs text-green-600 mt-1'>
																{coupon.type === TypesCoupon.DISCOUNT
																	? `${coupon.discount}% de descuento`
																	: `${formatCurrency(
																			coupon.amount || 0
																	  )} de descuento`}
															</p>
															{calculatedData.couponDiscount > 0 && (
																<p className='text-xs text-green-700 font-semibold mt-1'>
																	Ahorro:{' '}
																	{formatCurrency(
																		calculatedData.couponDiscount
																	)}
																</p>
															)}
														</div>
													</div>
													<button
														onClick={handleRemoveCoupon}
														className='p-1 hover:bg-green-200 rounded-lg transition-colors flex-shrink-0'
														aria-label='Remover cupón'
													>
														<X className='w-4 h-4 text-green-600' />
													</button>
												</div>
											</div>
										</div>
									) : (
										<div className='space-y-2'>
											<div className='flex flex-col xs:flex-row gap-2'>
												<input
													type='text'
													value={promoCode}
													onChange={(e) => setPromoCode(e.target.value)}
													placeholder='Código de cupón'
													className='flex-1 px-3 py-2 border border-red-200 rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-red-300 text-sm min-w-0'
													onKeyPress={(e) => {
														if (e.key === 'Enter') {
															handleApplyPromoCode()
														}
													}}
												/>
												<button
													onClick={handleApplyPromoCode}
													disabled={isCouponLoading || !promoCode.trim()}
													className='bg-red-600 hover:bg-red-700 text-white px-3 py-2 xs:px-4 xs:py-2 rounded-lg sm:rounded-xl font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1 whitespace-nowrap flex-shrink-0'
												>
													{isCouponLoading ? (
														<>
															<Loader2 className='w-4 h-4 animate-spin' />
															<span className='hidden xs:inline'>
																Aplicando...
															</span>
														</>
													) : (
														'Aplicar'
													)}
												</button>
											</div>
										</div>
									)}
								</div>

								<div className='bg-white rounded-3xl p-6 shadow-md'>
									<h4 className='font-bold text-gray-900 mb-4 text-sm'>
										Garantías
									</h4>
									<div className='space-y-3'>
										<div className='flex items-center gap-3'>
											<div className='w-8 h-8 bg-green-100 rounded-full flex items-center justify-center'>
												<Truck className='w-4 h-4 text-green-600' />
											</div>
											<span className='text-xs text-gray-600'>
												Envío gratis 120.000 COP
											</span>
										</div>
										<div className='flex items-center gap-3'>
											<div className='w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center'>
												<Shield className='w-4 h-4 text-blue-600' />
											</div>
											<span className='text-xs text-gray-600'>
												Compra segura
											</span>
										</div>
										<div className='flex items-center gap-3'>
											<div className='w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center'>
												<RotateCcw className='w-4 h-4 text-purple-600' />
											</div>
											<span className='text-xs text-gray-600'>
												Devolución hasta 7 por 7 dias
											</span>
										</div>
										<div className='flex items-center gap-3'>
											<div className='w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center'>
												<Star className='w-4 h-4 text-yellow-600' />
											</div>
											<span className='text-xs text-gray-600'>
												Calidad garantizada
											</span>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className='w-full py-[4vh] px-[2vw] lg:px-[6vw]'>
				<ProductSlider
					title='Explora más opciones'
					category='Otros'
					filteredProducts={products as FilteredProductsResponse}
					haveZone={false}
					customerId={customer?._id || ''}
					customer={customer}
				/>
			</div>
		</Layout>
	)
}

export default ShoppingCart
